import { FC, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { NotificationListItem, useNotifications } from '@/entities/notification';
import { media } from '@/shared/components';
import { EmptyList } from '@/shared/components/EmptyList';
import { COLORS } from '@/shared/constants/colors';
import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock';
import { useClickOutside } from '@/shared/hooks/useClickOutside';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Button } from '@/shared/uikitV2/Button';
import { Tabs } from '@/shared/uikitV2/Tabs';

import { NotificationItem } from './NotificationItem';
import { Dot } from './styled';

type Tab = 'new' | 'all';

type ChildrenProps = {
  toggleShow: () => void;
};

type NotificationsProps = {
  children: (props: ChildrenProps) => ReactNode;
};

const ANIMATION_OFFSET_Y = 12;
const PADDING_BOTTOM = 32;

export const Notifications: FC<NotificationsProps> = ({ children }) => {
  const { t } = useTranslation('features/notification');
  const { isDbReady } = useSjActions('notification');
  const { getNotificationsList, markAllRead } = useNotifications();
  const [show, toggleShow] = useToggle(false);
  const { ref } = useClickOutside({ show, onHide: toggleShow });

  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [notificationsList, setNotificationsList] = useState<NotificationListItem[]>([]);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useBodyScrollLock(show);

  const fetchNotifications = async () => {
    const items = await getNotificationsList();
    setNotificationsList(items);
  };

  useEffect(() => {
    if (isDbReady) {
      fetchNotifications();
    }
  }, [isDbReady]);

  useLayoutEffect(() => {
    if (show && wrapperRef.current) {
      const { top } = wrapperRef.current.getBoundingClientRect();
      wrapperRef.current.style.maxHeight = `calc(100dvh - ${top}px - ${PADDING_BOTTOM}px + ${ANIMATION_OFFSET_Y}px)`;
    }
  }, [show]);

  const filteredNotifications = useMemo(
    () => notificationsList.filter((item) => activeTab === 'all' || !item.isRead),
    [notificationsList, activeTab],
  );

  const hasUnread = useMemo(
    () => notificationsList.some((item) => !item.isRead),
    [notificationsList],
  );

  const tabs = [
    { key: 'new', label: t('tabs.new') },
    { key: 'all', label: t('tabs.all') },
  ];

  const hasNotifications = filteredNotifications.length > 0;

  const handleMarkAllRead = async () => {
    await markAllRead();
    await fetchNotifications();
  };

  return (
    <Container ref={ref}>
      {children({ toggleShow })}
      {hasUnread && <Dot className="error" />}
      {show && (
        <ContentWrapper ref={wrapperRef}>
          <Header>
            <Title bold size={18}>
              {t('title')}
            </Title>
            <Button
              type="text"
              size="middle"
              icon={<Icon name="check" size="s20" />}
              style={{ height: 20 }}
              disabled={!hasUnread}
              onClick={handleMarkAllRead}
            >
              {t('markAsRead')}
            </Button>
          </Header>
          <TabsContainer>
            <StyledTabs
              defaultValue={activeTab}
              items={tabs}
              onChange={(tab) => setActiveTab(tab as Tab)}
            />
          </TabsContainer>
          <ItemsContainer className="uikitScrollbar">
            {hasNotifications ? (
              filteredNotifications.map((item) => (
                <NotificationItem key={item.id} item={item} onMarkRead={fetchNotifications} />
              ))
            ) : (
              <StyledEmptyList icon="bell-ringing" title={t('noData')} message={t('noDataText')} />
            )}
          </ItemsContainer>
        </ContentWrapper>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const fadeIn = keyframes`
  from {
    transform: translateY(10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ContentWrapper = styled.div`
  width: 450px;
  position: absolute;
  top: 44px;
  right: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0px 4px 8px 0px ${COLORS.colorCardShadow};
  z-index: 10;
  animation: ${fadeIn} 100ms ease-in-out;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${media.sm} {
    --padding: 8px;
    position: fixed;
    width: calc(100% - (var(--padding) * 2));
    right: var(--padding);
  }
`;

const Header = styled.div`
  padding-inline: 24px;
  padding-top: 24px;
  padding-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
`;

const TabsContainer = styled.div`
  padding-inline: 24px;
  border-bottom: 1px solid ${COLORS.systemDark100};
`;

const StyledTabs = styled(Tabs)`
  &&& {
    .ant-tabs-tab {
      width: 100%;
    }
  }
`;

const ItemsContainer = styled.div`
  overflow: auto;
  flex: 1;
`;

const StyledEmptyList = styled(EmptyList)`
  min-height: 220px;
`;
